/**
 * Navigation allowlist for the in-app AI assistant.
 *
 * The navigate_to client tool may ONLY navigate to these app pages.
 * Used server-side (listed in the system prompt as a sitemap the model
 * learns from) and client-side (validation before router.push).
 * Admin pages are deliberately excluded.
 *
 * Paths may contain ONE `{id}` placeholder for record detail pages; the
 * segment is validated against a safe-id pattern before navigation.
 *
 * Pages also carry the SAME plan/permission gates as the app navigation
 * (navigationTree in get-first-accessible-page.ts): `requiredRole` is checked
 * with hasRole(), `feature` against the organization's merged plan features.
 * Pages failing either gate are excluded from the model's sitemap (server)
 * and rejected by the navigate_to handler (client).
 */

import { hasRole } from '@/lib/platform/utils/permissions-helpers';
import type { PermissionsData } from '@/lib/platform/navigation/get-first-accessible-page';

/** Plan feature flags relevant for AI page gating (subset of GatingFeatures). */
export interface AiGatingFeatures {
  sponsorshipSponsors?: boolean;
  sponsorshipPackages?: boolean;
  hasAdvancedReports?: boolean;
  restApi?: boolean;
  auditLog?: boolean;
  documents?: boolean;
}

/** A tab on a page, addressable as `{page.path}?tab={id}`. */
export interface AiPageTab {
  /** Tab id — must match the page's useTabParam ids exactly. */
  id: string;
  /** Human-readable tab name as shown in the tab bar. */
  label: string;
  /** What the tab shows (sitemap for the model). */
  description?: string;
  /** Help Center article for this tab (overrides the page's docPath). */
  docPath?: string;
}

export interface AiNavigablePage {
  /** App-relative path (no query/hash). May contain one `{id}` placeholder. */
  path: string;
  /** Human-readable page name shown to the model and in narration. */
  label: string;
  /** What the page shows + which data tool matches it (sitemap for the model). */
  description: string;
  /** Role required to access the page (same keys as navigationTree). */
  requiredRole?: string;
  /** Plan feature required to access the page (same keys as navigationTree). */
  feature?: keyof AiGatingFeatures;
  /** Best-matching Help Center article (help-articles.ts path) for this page. */
  docPath?: string;
  /**
   * URL-addressable tabs (`?tab=<id>`). Tabs inherit the page's gating.
   * Only static pages (no `{id}`) may declare tabs.
   */
  tabs?: AiPageTab[];
}

export const AI_NAVIGABLE_PAGES: AiNavigablePage[] = [
  // Valuations
  {
    path: '/products/valuations/dashboard',
    docPath: 'valuations/dashboard',
    label: 'Valuations dashboard',
    description:
      'Overview of the latest approved valuation: total value, sales value, media value, trends and charts.',
    requiredRole: 'dashboard',
  },
  {
    path: '/products/valuations/valuations',
    docPath: 'valuations/valuations-list',
    label: 'Valuations list',
    description:
      'All valuations with date, status (draft/approved), total value, sales value and media value. Data tool: list_valuations. Empty state: "Create First Valuation" button opens the new valuation form.',
  },
  {
    path: '/products/valuations/valuation/{id}',
    docPath: 'valuations/valuation-details',
    label: 'Valuation detail',
    description:
      'One valuation in full: financial metrics, media metrics with CPM rates, totals. Use the id from list_valuations/get_valuation. Data tool: get_valuation.',
  },
  {
    path: '/products/valuations/valuation/{id}/edit',
    docPath: 'valuations/create-valuation',
    label: 'Edit valuation form',
    description:
      'Edit form for one valuation (financial + media metrics, CPM rates). Navigate HERE when the user wants to change a valuation — they make the edits themselves on this page. Use the id from list_valuations/get_valuation.',
  },
  {
    path: '/products/valuations/valuation/new',
    docPath: 'valuations/create-valuation',
    label: 'New valuation form',
    description: 'Form to create a new valuation (financial + media metrics).',
  },
  {
    path: '/products/valuations/reports',
    docPath: 'valuations/reports',
    label: 'Valuation reports',
    description: 'Generated valuation reports and exports.',
    requiredRole: 'reports',
    feature: 'hasAdvancedReports',
  },
  {
    path: '/products/valuations/methods',
    docPath: 'valuations/methods',
    label: 'Valuation methods',
    description: 'Explains the valuation methodology and how values are calculated.',
    requiredRole: 'methods',
  },
  // Sponsorships
  {
    path: '/products/sponsorships/sponsors',
    docPath: 'sponsorships/sponsors',
    label: 'Sponsors',
    description:
      'All sponsors with contacts, status and linked packages. Data tools: list_sponsors, get_sponsor. Empty state: "Add First Sponsor" button (label follows the organization\'s sponsor alias) opens the create-sponsor form.',
    requiredRole: 'sponsors',
    feature: 'sponsorshipSponsors',
    tabs: [
      { id: 'sponsors', label: 'Sponsors', description: 'Sponsor list (default tab).' },
      { id: 'contacts', label: 'Contacts', description: 'Contact persons across all sponsors.' },
    ],
  },
  {
    path: '/products/sponsorships/packages',
    docPath: 'sponsorships/packages',
    label: 'Sponsorship packages',
    description:
      'All sponsorship packages with tier, price and contents. Data tool: list_packages. Empty state: "Create Package" button (label follows the organization\'s package alias).',
    requiredRole: 'packages',
    feature: 'sponsorshipPackages',
  },
  {
    path: '/products/sponsorships/packages/{id}/builder',
    docPath: 'sponsorships/packages',
    label: 'Package inclusions builder',
    description:
      'Edit which inventory items one sponsorship package includes and their quantities. ' +
      'Fillable form: one number field per item (quantity; 0 removes). The user saves with ' +
      'the "Save Inclusions" button. Use the id from list_packages.',
    requiredRole: 'packages',
    feature: 'sponsorshipPackages',
  },
  {
    path: '/products/sponsorships/packages/{id}/preview',
    docPath: 'sponsorships/packages',
    label: 'Package preview',
    description: 'Read-only preview of one sponsorship package. Use the id from list_packages.',
    requiredRole: 'packages',
    feature: 'sponsorshipPackages',
  },
  {
    path: '/products/sponsorships/proposals',
    docPath: 'sponsorships/proposals',
    label: 'Proposals',
    description:
      'Sponsorship proposals sent to sponsors and their status. Proposals require at least one package — the empty state links to the packages page.',
    requiredRole: 'proposals',
    feature: 'sponsorshipPackages',
  },
  {
    path: '/products/sponsorships/proposals/create',
    docPath: 'sponsorships/proposals',
    label: 'Create proposal',
    description: 'Form to create a new sponsorship proposal.',
    requiredRole: 'proposals',
    feature: 'sponsorshipPackages',
  },
  {
    path: '/products/sponsorships/inventory',
    docPath: 'sponsorships/inventory/inventory',
    label: 'Sponsorship inventory',
    description:
      'Inventory items, categories, tiers and events available for packages. Data tool: get_sponsorship_inventory. Empty state: "Add First Item" button.',
    requiredRole: 'inventory',
    feature: 'sponsorshipPackages',
    tabs: [
      { id: 'library', label: 'Inventory', description: 'Inventory item list (default tab).' },
      {
        id: 'settings',
        label: 'Settings',
        description: 'Manage inventory categories, tiers and events.',
        docPath: 'sponsorships/inventory/settings',
      },
    ],
  },
  {
    path: '/products/sponsorships/analytics',
    docPath: 'sponsorships/analytics',
    label: 'Sponsorship analytics',
    description:
      'Sponsorship analytics: revenue, package utilization, sponsor distribution. Data tool: get_analytics.',
    requiredRole: 'analytics',
    feature: 'sponsorshipPackages',
  },
  // Platform
  {
    path: '/platform/documents',
    docPath: 'platform/settings/documents',
    label: 'Documents',
    description:
      'Uploaded files and editor templates organized in folders. Data tools: list_documents, get_document_link.',
    requiredRole: 'documents',
    feature: 'documents',
    tabs: [
      {
        id: 'upload',
        label: 'Documents',
        description: 'Uploaded files and folders (default tab).',
      },
      { id: 'library', label: 'Document Templates', description: 'Editor document templates.' },
    ],
  },
  {
    path: '/platform/users',
    docPath: 'platform/team-management',
    label: 'Users & permissions',
    description: 'Organization members, invitations and their permissions.',
    requiredRole: 'users',
  },
  {
    path: '/platform/organisation',
    docPath: 'platform/company',
    label: 'Organisation settings',
    description: 'Organization profile, branding, label aliases and locale settings.',
    requiredRole: 'organisation',
    tabs: [
      {
        id: 'general',
        label: 'General',
        description: 'Organization name, logo, address and VAT (default tab).',
      },
      {
        id: 'sso',
        label: 'SSO',
        description: 'Single sign-on configuration.',
        docPath: 'platform/settings/sso',
      },
      { id: 'advanced', label: 'Advanced', description: 'Danger zone: delete organization.' },
    ],
  },
  {
    path: '/platform/settings',
    docPath: 'platform/settings/general',
    label: 'App settings',
    description: 'Personal app settings (appearance, notifications).',
    requiredRole: 'organisation',
    tabs: [
      {
        id: 'platform',
        label: 'Platform',
        description: 'Base currency, language, date/number formats, timezone (default tab).',
      },
      {
        id: 'valuations',
        label: 'Customize',
        description: 'Label aliases and default brand values for valuations.',
        docPath: 'platform/settings/brand-values',
      },
      {
        id: 'sponsorships',
        label: 'Negotiator',
        description: 'Negotiator settings (requires the sponsorship packages feature).',
        docPath: 'platform/settings/negotiator',
      },
      {
        id: 'cpm-benchmarks',
        label: 'CPM Benchmarks',
        description: 'CPM benchmark rates per media channel.',
        docPath: 'guides/valuations/cpm-benchmarks',
      },
      {
        id: 'currency-conversion',
        label: 'Currency Conversion',
        description: 'Exchange rates used for currency conversion.',
        docPath: 'guides/valuations/currency-conversion',
      },
    ],
  },
  {
    path: '/platform/billing',
    docPath: 'platform/billing/invoices',
    label: 'Billing',
    description: 'Subscription plan, add-ons and invoices. Where plan upgrades happen.',
    requiredRole: 'billing',
    tabs: [
      {
        id: 'billing',
        label: 'Subscription',
        description: 'Current plan, seats and add-ons (default tab).',
      },
      { id: 'usage', label: 'Usage', description: 'Plan usage and limits.' },
      {
        id: 'payment-methods',
        label: 'Payment Methods',
        description: 'Saved payment methods.',
        docPath: 'platform/billing/payment-methods',
      },
      {
        id: 'invoices',
        label: 'Invoices',
        description: 'Invoice history and downloads.',
        docPath: 'platform/billing/invoices',
      },
      {
        id: 'billing-details',
        label: 'Billing Details',
        description: 'Billing address and company details (not in the tab bar — link only).',
        docPath: 'platform/billing/billing-details',
      },
    ],
  },
  {
    path: '/platform/profile',
    docPath: 'platform/user-profile',
    label: 'My profile',
    description: 'The signed-in user’s own profile.',
  },
  {
    path: '/platform/api',
    docPath: 'platform/api',
    label: 'API & MCP settings',
    description: 'REST API keys and MCP server access.',
    requiredRole: 'api',
    feature: 'restApi',
    tabs: [
      { id: 'keys', label: 'API Keys', description: 'REST API keys (default tab).' },
      { id: 'usage', label: 'Usage', description: 'API usage statistics.' },
      { id: 'documentation', label: 'Documentation', description: 'REST API documentation.' },
      {
        id: 'mcp',
        label: 'MCP',
        description: 'MCP server connection setup.',
        docPath: 'platform/mcp',
      },
    ],
  },
  {
    path: '/platform/audit',
    label: 'Audit log',
    description: 'Audit trail of data changes in the organization.',
    requiredRole: 'audit_log',
    feature: 'auditLog',
  },
  {
    path: '/platform/plugins',
    docPath: 'platform/plugins',
    label: 'Plugins',
    description: 'Available plugins and integrations.',
    requiredRole: 'plugins',
  },
];

/** Safe record-id segment (UUIDs and similar — no slashes, dots or query chars). */
const SAFE_ID = /^[A-Za-z0-9_-]+$/;

const STATIC_PATHS = new Set(
  AI_NAVIGABLE_PAGES.filter((p) => !p.path.includes('{id}')).map((p) => p.path)
);

const DYNAMIC_PAGES = AI_NAVIGABLE_PAGES.filter((p) => p.path.includes('{id}')).map((p) => {
  const [prefix, suffix] = p.path.split('{id}');
  return { page: p, prefix, suffix };
});

/** Extract the `{id}` value when the path matches a dynamic page pattern. */
function matchDynamic(path: string): { page: AiNavigablePage; id: string } | undefined {
  for (const { page, prefix, suffix } of DYNAMIC_PAGES) {
    if (!path.startsWith(prefix)) continue;
    if (suffix && !path.endsWith(suffix)) continue;
    const id = path.slice(prefix.length, suffix ? path.length - suffix.length : undefined);
    if (SAFE_ID.test(id)) return { page, id };
  }
  return undefined;
}

/**
 * Split an optional `?tab=<id>` suffix off a path. Returns undefined when the
 * query is anything OTHER than a single `tab` param whose id is declared on
 * the base page's tabs (arbitrary query strings are never navigable). Hashes
 * are always rejected.
 */
function splitTabQuery(path: string): { base: string; tab?: AiPageTab } | undefined {
  if (path.includes('#')) return undefined;
  const q = path.indexOf('?');
  if (q === -1) return { base: path };
  const base = path.slice(0, q);
  const params = new URLSearchParams(path.slice(q + 1));
  const tabId = params.get('tab');
  if (!tabId || [...params.keys()].some((key) => key !== 'tab')) return undefined;
  // Tabs only exist on static pages (base must be an exact sitemap path)
  const page = AI_NAVIGABLE_PAGES.find((p) => p.path === base);
  const tab = page?.tabs?.find((t) => t.id === tabId);
  return tab ? { base, tab } : undefined;
}

/** True when the path is an allowlisted AI navigation target. */
export function isAiNavigablePath(path: string): boolean {
  const split = splitTabQuery(path);
  if (!split) return false;
  if (STATIC_PATHS.has(split.base)) return true;
  return matchDynamic(split.base) !== undefined;
}

/** Full page entry for a known app path (static or dynamic match, `?tab=` allowed). */
export function getAiPage(path: string): AiNavigablePage | undefined {
  const split = splitTabQuery(path);
  if (!split) return undefined;
  const exact = AI_NAVIGABLE_PAGES.find((p) => p.path === split.base);
  if (exact) return exact;
  return matchDynamic(split.base)?.page;
}

/** Help Center article for a path: the tab's docPath when set, else the page's. */
export function getAiDocPath(path: string): string | undefined {
  const split = splitTabQuery(path);
  if (!split) return undefined;
  return split.tab?.docPath ?? getAiPage(split.base)?.docPath;
}

/**
 * True when the page passes BOTH gates (role + plan feature) — same logic as
 * filterNavigationTree. Unknown paths return false.
 */
export function isAiPageAllowed(
  path: string,
  permissions: PermissionsData | null | undefined,
  features: AiGatingFeatures
): boolean {
  const page = getAiPage(path);
  if (!page) return false;
  if (page.feature && !features[page.feature]) return false;
  if (page.requiredRole && !hasRole(permissions, page.requiredRole)) return false;
  return true;
}

/** Pages the user can NOT access (for the model's "never suggest" list). */
export function getAiUnavailablePages(
  permissions: PermissionsData | null | undefined,
  features: AiGatingFeatures
): AiNavigablePage[] {
  return AI_NAVIGABLE_PAGES.filter((page) => !isAiPageAllowed(page.path, permissions, features));
}

/** Human label for a known app path (prefix match for dynamic subpages). */
export function getAiPageLabel(path: string): string | undefined {
  const split = splitTabQuery(path);
  if (!split) return undefined;
  const exact = AI_NAVIGABLE_PAGES.find((p) => p.path === split.base);
  if (exact) return split.tab ? `${exact.label} — ${split.tab.label}` : exact.label;
  const dynamic = matchDynamic(split.base);
  if (dynamic) return dynamic.page.label;
  const prefix = AI_NAVIGABLE_PAGES.find(
    (p) => !p.path.includes('{id}') && split.base.startsWith(p.path + '/')
  );
  return prefix?.label;
}
